import React, { useState, useEffect, useCallback } from "react";
import {
    Box,
    Typography,
    Button,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Select,
    MenuItem,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import { authHeader, clearToken } from "../../auth";

// Colonnes des fichiers fédéraux (FIS) — voir Transponder/Bib/Last
// Name/First Name/Code/Gender/Year/Category/Nation/Club/From list/
// Points/Association.
const expectedFields = [
    "bib",
    "transponder",
    "fiscode", // "Code"
    "lastname", // "Last Name"
    "firstname", // "First Name"
    "gender",
    "birthyear", // "Year"
    "category",
    "nation",
    "club",
    "fromlist", // "From list"
    "points",
    "association",
];

const STORAGE_KEY = "timingAthletesMapping";
const CUSTOM_STORAGE_KEY = "timingAthletesCustomFields";

export default function TimingAthletesImport() {
    const navigate = useNavigate();
    const { eventKey } = useParams();

    const [rows2D, setRows2D] = useState([]);
    const [headers, setHeaders] = useState([]);
    const [mapping, setMapping] = useState({});
    const [customFields, setCustomFields] = useState([]); // ["nom_champ", ...]
    const [ignoreRows, setIgnoreRows] = useState(0);
    const [mappedData, setMappedData] = useState([]);

    const [importedAthletes, setImportedAthletes] = useState([]);
    const [error, setError] = useState("");

    // -----------------------------
    // API FETCH AVEC TOKEN (authHeader())
    // -----------------------------
    const apiFetch = useCallback(
        async (url, options = {}) => {
            const res = await fetch(url, {
                ...options,
                headers: {
                    "Content-Type": "application/json",
                    ...authHeader(),
                    ...(options.headers || {}),
                },
            });

            if (res.status === 401) {
                clearToken();
                navigate("/login");
                return null;
            }

            return res;
        },
        [navigate]
    );

    // -----------------------------
    // Charger le mapping sauvegardé
    // -----------------------------
    useEffect(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            const { ignoreRows: savedIgnore, mapping: savedMapping } = JSON.parse(saved);
            setIgnoreRows(savedIgnore ?? 0);
            setMapping(savedMapping ?? {});
        }

        const savedCustom = localStorage.getItem(CUSTOM_STORAGE_KEY);
        if (savedCustom) {
            setCustomFields(JSON.parse(savedCustom));
        }
    }, []);

    const addCustomField = () => {
        const name = window.prompt("Nom du champ personnalisé (ex: date_naissance_precise)");
        if (!name || !name.trim()) return;
        const key = name.trim().toLowerCase().replace(/\s+/g, "_");
        if (expectedFields.includes(key) || customFields.includes(key)) {
            window.alert("Ce champ existe déjà.");
            return;
        }
        const updated = [...customFields, key];
        setCustomFields(updated);
        localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(updated));
    };

    const removeCustomField = (key) => {
        const updated = customFields.filter((f) => f !== key);
        setCustomFields(updated);
        localStorage.setItem(CUSTOM_STORAGE_KEY, JSON.stringify(updated));
        setMapping((prev) => {
            const { [key]: _removed, ...rest } = prev;
            return rest;
        });
    };

    // -----------------------------
    // 1. Lire le fichier Excel brut
    // -----------------------------
    const handleFileSelect = async (file) => {
        const data = await file.arrayBuffer();
        const workbook = XLSX.read(data, { type: "array" });
        const sheet = workbook.Sheets[workbook.SheetNames[0]];

        const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: "" });

        setRows2D(rows);
        setHeaders(rows[ignoreRows] || []);
        setMappedData([]);
    };

    // -----------------------------
    // 2. Recalcul des headers selon ignoreRows
    // -----------------------------
    useEffect(() => {
        if (rows2D.length > 0) {
            setHeaders(rows2D[ignoreRows] || []);
        }
    }, [ignoreRows, rows2D]);

    // -----------------------------
    // 3. Appliquer le mapping
    // -----------------------------
    const applyMapping = () => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({ ignoreRows, mapping }));

        if (rows2D.length === 0) return;

        const headerRow = rows2D[ignoreRows];
        const dataRows = rows2D.slice(ignoreRows + 1);

        const mapped = dataRows
            .map((row) => {
                const obj = {};

                [...expectedFields, ...customFields].forEach((field) => {
                    const colName = mapping[field];
                    const colIndex = headerRow.indexOf(colName);

                    let value = colIndex >= 0 ? row[colIndex] : null;
                    if (value === "") value = null;

                    obj[field] = value;
                });

                return obj;
            })
            // Ignore les lignes complètement vides (souvent en fin de
            // feuille Excel).
            .filter((obj) => Object.values(obj).some((v) => v !== null && v !== undefined));

        setMappedData(mapped);
    };

    // -----------------------------
    // 4. Envoyer au backend
    // -----------------------------
    const sendToBackend = async () => {
        if (mappedData.length === 0) return;
        setError("");

        const res = await apiFetch(`/api/timing/athletes/import/${encodeURIComponent(eventKey)}`, {
            method: "POST",
            body: JSON.stringify(mappedData),
        });

        if (!res) return;

        if (!res.ok) {
            const data = await res.json().catch(() => ({}));
            setError(data.error || `HTTP ${res.status}`);
            return;
        }

        const data = await res.json();
        setImportedAthletes(data[eventKey] || []);
    };

    // -----------------------------
    // Rendu
    // -----------------------------
    return (
        <Box sx={{ p: 2 }}>
            <Typography variant="h5" gutterBottom>
                Import concurrents (Excel) — {eventKey}
            </Typography>

            {error && (
                <Typography color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
            )}

            {/* 1. Sélection du fichier */}
            <input
                type="file"
                accept=".xlsx,.xls"
                onChange={(e) => {
                    const file = e.target.files[0];
                    if (!file) return;
                    handleFileSelect(file);
                }}
            />

            {/* 2. Mapping */}
            {rows2D.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6">Mapping des colonnes</Typography>

                    <Box sx={{ mt: 2, mb: 2, display: "flex", alignItems: "center", gap: 2 }}>
                        <Typography variant="subtitle1">
                            Ligne des en-têtes (ignorer les lignes avant)
                        </Typography>

                        <Select
                            value={ignoreRows}
                            onChange={(e) => setIgnoreRows(Number(e.target.value))}
                            size="small"
                        >
                            {[0, 1, 2, 3, 4, 5].map((n) => (
                                <MenuItem key={n} value={n}>
                                    {n}
                                </MenuItem>
                            ))}
                        </Select>
                    </Box>

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Champ attendu (backend)</TableCell>
                                <TableCell>Colonne Excel</TableCell>
                                <TableCell></TableCell>
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {expectedFields.map((field) => (
                                <TableRow key={field}>
                                    <TableCell>{field}</TableCell>
                                    <TableCell>
                                        <Select
                                            value={mapping[field] || ""}
                                            onChange={(e) =>
                                                setMapping((prev) => ({
                                                    ...prev,
                                                    [field]: e.target.value,
                                                }))
                                            }
                                            fullWidth
                                            displayEmpty
                                        >
                                            <MenuItem value="">
                                                <em>Non utilisé</em>
                                            </MenuItem>

                                            {headers.map((h) => (
                                                <MenuItem key={h} value={h}>
                                                    {h}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell />
                                </TableRow>
                            ))}

                            {customFields.map((field) => (
                                <TableRow key={field}>
                                    <TableCell>
                                        {field} <em style={{ color: "#888" }}>(personnalisé)</em>
                                    </TableCell>
                                    <TableCell>
                                        <Select
                                            value={mapping[field] || ""}
                                            onChange={(e) =>
                                                setMapping((prev) => ({
                                                    ...prev,
                                                    [field]: e.target.value,
                                                }))
                                            }
                                            fullWidth
                                            displayEmpty
                                        >
                                            <MenuItem value="">
                                                <em>Non utilisé</em>
                                            </MenuItem>

                                            {headers.map((h) => (
                                                <MenuItem key={h} value={h}>
                                                    {h}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </TableCell>
                                    <TableCell>
                                        <Button size="small" color="error" onClick={() => removeCustomField(field)}>
                                            Retirer
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>

                    <Button variant="outlined" sx={{ mt: 2, mr: 2 }} onClick={addCustomField}>
                        + Champ personnalisé
                    </Button>
                    <Button variant="contained" sx={{ mt: 2 }} onClick={applyMapping}>
                        Prévisualiser
                    </Button>
                </Box>
            )}

            {/* 3. Prévisualisation */}
            {mappedData.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="h6">Prévisualisation ({mappedData.length} concurrents)</Typography>

                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                {[...expectedFields, ...customFields].map((field) => (
                                    <TableCell key={field}>{field}</TableCell>
                                ))}
                            </TableRow>
                        </TableHead>

                        <TableBody>
                            {mappedData.slice(0, 10).map((r, i) => (
                                <TableRow key={i}>
                                    {[...expectedFields, ...customFields].map((field) => (
                                        <TableCell key={field}>{r[field]}</TableCell>
                                    ))}
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {mappedData.length > 10 && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            ... et {mappedData.length - 10} de plus
                        </Typography>
                    )}

                    <Button variant="contained" sx={{ mt: 2 }} onClick={sendToBackend}>
                        Envoyer au backend
                    </Button>
                </Box>
            )}

            {/* 4. Confirmation */}
            {importedAthletes.length > 0 && (
                <Box sx={{ mt: 4 }}>
                    <Typography variant="h6" color="success.main">
                        ✓ {importedAthletes.length} concurrents importés.
                    </Typography>
                    <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate("/timingsoftware")}>
                        Retour au module Timing Software
                    </Button>
                    <Button
                        variant="contained"
                        sx={{ mt: 2, ml: 2 }}
                        onClick={() => navigate(`/timingsoftware/startlist/${encodeURIComponent(eventKey)}`)}
                    >
                        Générer la liste de départ
                    </Button>
                    <Button
                        variant="outlined"
                        sx={{ mt: 2, ml: 2 }}
                        onClick={() => navigate(`/timingsoftware/athletes/${encodeURIComponent(eventKey)}`)}
                    >
                        Voir / éditer la liste des participants
                    </Button>
                </Box>
            )}
        </Box>
    );
}